export interface BadgeDefinition {
  slug: string;
  name: string;
  description: string;
  icon: string;
  category: 'activity' | 'streak' | 'communication' | 'service' | 'quality' | 'community' | 'profile' | 'progression';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  requirement?: string;
}

export interface UserBadge {
  badge_slug: string;
  earned_at: string;
}

// Comprehensive badge definitions organized by category
export const BADGE_DEFINITIONS: BadgeDefinition[] = [
  // Activity Badges (Common to Uncommon)
  {
    slug: 'first_post',
    name: 'First Steps',
    description: 'Created your first service',
    icon: '📝',
    category: 'activity',
    rarity: 'common',
    requirement: 'Create 1 service'
  },
  {
    slug: 'helper',
    name: 'Helper',
    description: 'Completed your first service',
    icon: '🤝',
    category: 'activity',
    rarity: 'common',
    requirement: 'Complete 1 service'
  },

  // Communication Badges
  {
    slug: 'chatty',
    name: 'Chatty',
    description: 'Sent 10 messages',
    icon: '💬',
    category: 'communication',
    rarity: 'common',
    requirement: 'Send 10 messages'
  },
  {
    slug: 'communicator',
    name: 'Communicator',
    description: 'Sent 50 messages',
    icon: '📢',
    category: 'communication',
    rarity: 'uncommon',
    requirement: 'Send 50 messages'
  },
  {
    slug: 'social_star',
    name: 'Social Star',
    description: 'Sent 100 messages',
    icon: '⭐',
    category: 'communication',
    rarity: 'rare',
    requirement: 'Send 100 messages'
  },
  {
    slug: 'networker',
    name: 'Networker',
    description: 'Joined 5 chat rooms',
    icon: '🌐',
    category: 'communication',
    rarity: 'uncommon',
    requirement: 'Join 5 chat rooms'
  },

  // Streak Badges
  {
    slug: 'streak_3',
    name: 'Getting Started',
    description: '3-day activity streak',
    icon: '🔥',
    category: 'streak',
    rarity: 'common',
    requirement: '3-day streak'
  },
  {
    slug: 'streak_7',
    name: 'Week Warrior',
    description: '7-day activity streak',
    icon: '🔥',
    category: 'streak',
    rarity: 'uncommon',
    requirement: '7-day streak'
  },
  {
    slug: 'streak_14',
    name: 'Dedication Master',
    description: '14-day activity streak',
    icon: '🏆',
    category: 'streak',
    rarity: 'rare',
    requirement: '14-day streak'
  },
  {
    slug: 'streak_30',
    name: 'Unstoppable Force',
    description: '30-day activity streak',
    icon: '💪',
    category: 'streak',
    rarity: 'epic',
    requirement: '30-day streak'
  },

  // Service Provider Badges
  {
    slug: 'entrepreneur',
    name: 'Entrepreneur',
    description: 'Created 5 services',
    icon: '💼',
    category: 'service',
    rarity: 'uncommon',
    requirement: 'Create 5 services'
  },
  {
    slug: 'business_builder',
    name: 'Business Builder',
    description: 'Created 10 services',
    icon: '🏢',
    category: 'service',
    rarity: 'rare',
    requirement: 'Create 10 services'
  },
  {
    slug: 'service_empire',
    name: 'Service Empire',
    description: 'Created 25 services',
    icon: '🏰',
    category: 'service',
    rarity: 'epic',
    requirement: 'Create 25 services'
  },
  {
    slug: 'reliable_provider',
    name: 'Reliable Provider',
    description: 'Completed 5 services',
    icon: '✅',
    category: 'service',
    rarity: 'uncommon',
    requirement: 'Complete 5 services'
  },
  {
    slug: 'service_master',
    name: 'Service Master',
    description: 'Completed 20 services',
    icon: '👑',
    category: 'service',
    rarity: 'epic',
    requirement: 'Complete 20 services'
  },
  {
    slug: 'service_legend',
    name: 'Service Legend',
    description: 'Completed 50 services',
    icon: '🌟',
    category: 'service',
    rarity: 'legendary',
    requirement: 'Complete 50 services'
  },
  {
    slug: 'quick_delivery',
    name: 'Quick Delivery',
    description: 'Completed 3 services within 24 hours',
    icon: '⚡',
    category: 'service',
    rarity: 'rare',
    requirement: 'Complete 3 services in 24h'
  },
  {
    slug: 'service_specialist',
    name: 'Service Specialist',
    description: 'Completed 10 services in one category',
    icon: '🎯',
    category: 'service',
    rarity: 'rare',
    requirement: 'Complete 10 services in one category'
  },
  {
    slug: 'premium_provider',
    name: 'Premium Provider',
    description: 'Earned $500+ from services',
    icon: '💰',
    category: 'service',
    rarity: 'epic',
    requirement: 'Earn $500+ from services'
  },
  {
    slug: 'service_innovator',
    name: 'Service Innovator',
    description: 'Created services in 5 different categories',
    icon: '🚀',
    category: 'service',
    rarity: 'rare',
    requirement: 'Create services in 5 categories'
  },

  // Quality Badges
  {
    slug: 'four_star_achiever',
    name: 'Four Star Achiever',
    description: 'Received 5 four-star+ reviews',
    icon: '⭐',
    category: 'quality',
    rarity: 'uncommon',
    requirement: 'Get 5 four-star+ reviews'
  },
  {
    slug: 'four_star_expert',
    name: 'Four Star Expert',
    description: 'Received 15 four-star+ reviews',
    icon: '🌟',
    category: 'quality',
    rarity: 'rare',
    requirement: 'Get 15 four-star+ reviews'
  },
  {
    slug: 'four_star_master',
    name: 'Four Star Master',
    description: 'Received 30 four-star+ reviews',
    icon: '💫',
    category: 'quality',
    rarity: 'epic',
    requirement: 'Get 30 four-star+ reviews'
  },
  {
    slug: 'five_star_hero',
    name: 'Five Star Hero',
    description: 'Received 3 five-star reviews',
    icon: '⭐',
    category: 'quality',
    rarity: 'rare',
    requirement: 'Get 3 five-star reviews'
  },
  {
    slug: 'excellence_expert',
    name: 'Excellence Expert',
    description: 'Received 10 five-star reviews',
    icon: '🌟',
    category: 'quality',
    rarity: 'epic',
    requirement: 'Get 10 five-star reviews'
  },
  {
    slug: 'perfection_master',
    name: 'Perfection Master',
    description: 'Received 25 five-star reviews',
    icon: '👑',
    category: 'quality',
    rarity: 'legendary',
    requirement: 'Get 25 five-star reviews'
  },
  {
    slug: 'consistent_quality',
    name: 'Consistent Quality',
    description: 'Maintained 4.5+ average rating with 10+ reviews',
    icon: '🏆',
    category: 'quality',
    rarity: 'epic',
    requirement: 'Maintain 4.5+ average with 10+ reviews'
  },

  // Community Badges
  {
    slug: 'community_helper',
    name: 'Community Helper',
    description: 'Helped 5 different users',
    icon: '🤲',
    category: 'community',
    rarity: 'uncommon',
    requirement: 'Help 5 users'
  },
  {
    slug: 'mentor',
    name: 'Mentor',
    description: 'Helped 15 different users',
    icon: '🧠',
    category: 'community',
    rarity: 'rare',
    requirement: 'Help 15 users'
  },
  {
    slug: 'feedback_giver',
    name: 'Feedback Giver',
    description: 'Left 5 reviews',
    icon: '📝',
    category: 'community',
    rarity: 'uncommon',
    requirement: 'Leave 5 reviews'
  },

  // Profile Badges
  {
    slug: 'profile_complete',
    name: 'Complete Profile',
    description: 'Filled out your complete profile',
    icon: '📋',
    category: 'profile',
    rarity: 'common',
    requirement: 'Complete profile info'
  },
  {
    slug: 'picture_perfect',
    name: 'Picture Perfect',
    description: 'Added a profile picture',
    icon: '📷',
    category: 'profile',
    rarity: 'common',
    requirement: 'Add profile picture'
  },
  {
    slug: 'portfolio_builder',
    name: 'Portfolio Builder',
    description: 'Added 3 portfolio items',
    icon: '🎨',
    category: 'profile',
    rarity: 'uncommon',
    requirement: 'Add 3 portfolio items'
  },

  // Progression Badges
  {
    slug: 'xp_collector',
    name: 'XP Collector',
    description: 'Earned 500 XP',
    icon: '💎',
    category: 'progression',
    rarity: 'uncommon',
    requirement: 'Earn 500 XP'
  },
  {
    slug: 'xp_master',
    name: 'XP Master',
    description: 'Earned 2000 XP',
    icon: '💰',
    category: 'progression',
    rarity: 'rare',
    requirement: 'Earn 2000 XP'
  },
  {
    slug: 'level_up_star',
    name: 'Rising Star',
    description: 'Reached level 3',
    icon: '🌟',
    category: 'progression',
    rarity: 'uncommon',
    requirement: 'Reach level 3'
  },
  {
    slug: 'elite_player',
    name: 'Elite Player',
    description: 'Reached level 10',
    icon: '👑',
    category: 'progression',
    rarity: 'legendary',
    requirement: 'Reach level 10'
  }
];

export const getBadgeDefinition = (slug: string): BadgeDefinition | undefined => {
  return BADGE_DEFINITIONS.find(badge => badge.slug === slug);
};

export const getBadgesByCategory = (category: BadgeDefinition['category']): BadgeDefinition[] => {
  return BADGE_DEFINITIONS.filter(badge => badge.category === category);
};

export const getBadgeRarityColor = (rarity: BadgeDefinition['rarity']): string => {
  switch (rarity) {
    case 'common': return 'from-gray-400 to-gray-600';
    case 'uncommon': return 'from-green-400 to-green-600';
    case 'rare': return 'from-blue-400 to-blue-600';
    case 'epic': return 'from-purple-400 to-purple-600';
    case 'legendary': return 'from-yellow-400 to-orange-600';
    default: return 'from-gray-400 to-gray-600';
  }
};